import { ghostBtnClass, mfgInlineInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type {
  MfgMaterial,
  MfgMaterialUse,
  MfgRecipeLineRole,
  MfgRecipeSummary,
} from "@/lib/manufacturingClient";
import { coerceBomQuantityDraft, type BomLineSign } from "@/lib/manufacturing/bomLineSign";
import { ManufacturingComboboxPicker, type ComboboxOption } from "./ManufacturingComboboxPicker";
import { ManufacturingMarkMaterialMissingControl } from "./ManufacturingMarkMaterialMissingControl";
import { SlotLabelCombobox } from "./SlotLabelCombobox";
import type { MfgModelsLineRow } from "./useManufacturingModelsLines";

const ROLE_OPTIONS: Array<{ id: MfgRecipeLineRole; label: string }> = [
  { id: "structural", label: "Structural" },
  { id: "standard_option", label: "Standard option" },
  { id: "upgrade_option", label: "Upgrade" },
];

type LineActions = {
  setLineKind: (key: string, kind: "material" | "component" | "use") => void;
  setLineMaterial: (key: string, materialId: string) => void;
  setLineUse: (key: string, useId: string) => void;
  setLineComponentRecipe: (key: string, recipeId: string) => void;
  setLineQuantity: (key: string, quantity: string) => void;
  setLineRole: (key: string, role: MfgRecipeLineRole) => void;
  setLineSlotLabel: (key: string, slotLabel: string) => void;
  setLineUpgradePriceDelta: (key: string, value: string) => void;
  removeLine: (key: string) => void;
};

type Props = {
  row: MfgModelsLineRow;
  sign: BomLineSign;
  readOnly: boolean;
  selectedId: string;
  materialOptions: ComboboxOption<MfgMaterial>[];
  recipeOptions: ComboboxOption<MfgRecipeSummary>[];
  useOptions: ComboboxOption<MfgMaterialUse>[];
  orgSlotLabels: readonly string[];
  onError: (message: string) => void;
  onSaved: () => void;
  actions: LineActions;
};

function draftQuantity(quantity: string, sign: BomLineSign): number {
  const n = Number(quantity);
  if (sign === "negative") return Number.isFinite(n) && n < 0 ? n : -1;
  return n > 0 ? n : 1;
}

export function ManufacturingModelsBomLineRow({
  row,
  sign,
  readOnly,
  selectedId,
  materialOptions,
  recipeOptions,
  useOptions,
  orgSlotLabels,
  onError,
  onSaved,
  actions,
}: Props) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 py-2 last:border-b-0 dark:border-zinc-700">
      <select
        className={`${mfgInlineInputClass} min-w-[110px]`}
        value={row.line_kind}
        onChange={(e) => {
          const v = e.target.value;
          actions.setLineKind(
            row.key,
            v === "component" ? "component" : v === "use" ? "use" : "material",
          );
        }}
        disabled={readOnly}
      >
        <option value="material">Material</option>
        <option value="component">Sub-recipe</option>
        <option value="use">Use (job)</option>
      </select>

      {row.line_kind === "material" ? (
        <div className="min-w-[180px] flex-1 space-y-1">
          <ManufacturingComboboxPicker<MfgMaterial>
            value={row.material_id}
            options={materialOptions}
            onPick={(opt) => actions.setLineMaterial(row.key, opt.key)}
            placeholder="Select material…"
            disabled={readOnly}
          />
          {!readOnly && !row.material_id ? (
            <ManufacturingMarkMaterialMissingControl
              ownerKind="recipe"
              recipeId={selectedId}
              quantity={draftQuantity(row.quantity, sign)}
              role={row.role}
              slotLabel={row.slot_label}
              onError={onError}
              onCreated={() => {
                actions.removeLine(row.key);
                onSaved();
              }}
            />
          ) : null}
        </div>
      ) : row.line_kind === "use" ? (
        <div className="min-w-[180px] flex-1">
          <ManufacturingComboboxPicker<MfgMaterialUse>
            value={row.material_use_id}
            options={useOptions}
            onPick={(opt) => actions.setLineUse(row.key, opt.key)}
            placeholder="Select job / use…"
            disabled={readOnly}
          />
        </div>
      ) : (
        <div className="min-w-[180px] flex-1">
          <ManufacturingComboboxPicker<MfgRecipeSummary>
            value={row.component_recipe_id}
            options={recipeOptions}
            onPick={(opt) => actions.setLineComponentRecipe(row.key, opt.key)}
            placeholder="Select sub-recipe…"
            disabled={readOnly}
          />
        </div>
      )}

      <input
        className={`${mfgInlineInputClass} max-w-[90px]`}
        inputMode="decimal"
        placeholder={row.line_kind === "use" ? "Use qty" : "Qty"}
        title={
          sign === "negative"
            ? "Saved quantity. Stays negative."
            : row.line_kind === "use"
              ? "Quantity in use units"
              : undefined
        }
        value={row.quantity}
        onChange={(e) => {
          const next = coerceBomQuantityDraft(e.target.value, sign);
          if (next == null) return;
          actions.setLineQuantity(row.key, next);
        }}
        disabled={readOnly}
      />

      <select
        className={`${mfgInlineInputClass} min-w-[140px]`}
        value={row.role}
        onChange={(e) => actions.setLineRole(row.key, e.target.value as MfgRecipeLineRole)}
        disabled={readOnly}
      >
        {ROLE_OPTIONS.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label}
          </option>
        ))}
      </select>

      <SlotLabelCombobox
        value={row.slot_label}
        suggestions={orgSlotLabels}
        disabled={readOnly}
        onChange={(v) => actions.setLineSlotLabel(row.key, v)}
      />

      {row.role === "upgrade_option" ? (
        <input
          className={`${mfgInlineInputClass} max-w-[110px]`}
          inputMode="decimal"
          placeholder="$ upgrade"
          value={row.upgrade_price_delta}
          onChange={(e) => actions.setLineUpgradePriceDelta(row.key, e.target.value)}
          disabled={readOnly}
        />
      ) : null}

      {readOnly ? null : (
        <button type="button" className={ghostBtnClass} onClick={() => actions.removeLine(row.key)}>
          Remove
        </button>
      )}
    </div>
  );
}
